import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { VStack, Input, Field, Spinner, Center, Text, HStack, createListCollection } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useGetRegCustomerQuery, usePatchRegCustomerMutation } from '../../../../api';
import { alertManager } from '../../../../utils/alertManager';
import { hasDiff } from '../../../../utils/hasDiff';
import { getRegistrationCountries } from '../../../../config/registrationCountries';
import BaseSelect from '../../../../components/base/BaseSelect';
import {
    createRegistrationAddressDetailsSchema,
    RegistrationAddressDetailsFormData,
} from '../../../../schemas/registrationSchema';
import Button from '../../../../components/base/Button';
import type { PatchRegCustomer } from '../../../../api/registration/types';

type RegistrationStepProps = {
    onBack: () => void;
    onNext: () => void;
};

export const StepAddressDetails: React.FC<RegistrationStepProps> = ({ onBack, onNext }) => {
    const { t, currentLanguage } = useTranslation();
    const { data: regCustomer, isLoading } = useGetRegCustomerQuery();
    const [patchRegCustomer, { isLoading: isPatching }] = usePatchRegCustomerMutation();

    const countryOptions = useMemo(() => getRegistrationCountries(currentLanguage), [currentLanguage]);
    const defaultCountry = countryOptions[0].value;
    const {
        register,
        reset,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<RegistrationAddressDetailsFormData>({
        resolver: yupResolver(createRegistrationAddressDetailsSchema(t)),
        mode: 'onBlur',
        defaultValues: {
            country: defaultCountry,
        },
    });
    const appliedFromApi = useRef(false);

    useEffect(() => {
        if (isLoading || !regCustomer || appliedFromApi.current) {
            return;
        }
        appliedFromApi.current = true;
        reset({
            country: regCustomer.personal_address?.country || defaultCountry,
            city: regCustomer.personal_address?.city ?? '',
            line: regCustomer.personal_address?.line ?? '',
            postcode: regCustomer.personal_address?.postcode ?? '',
        });
    }, [currentLanguage, isLoading, regCustomer, reset]);

    const countryCollection = useMemo(
        () =>
            createListCollection({
                items: countryOptions.map((country, index) => ({
                    key: `${index}_${country.value}`,
                    value: country.value,
                    label: country.label,
                })),
            }),
        [countryOptions],
    );

    const selectedCountry = watch('country');

    const handleNextClick = useCallback(() => {
        void handleSubmit(async ({ country, city, line, postcode }) => {
            if (isPatching) return;

            const patchPayload: PatchRegCustomer = {
                personal_address: {
                    country,
                    city,
                    line,
                    postcode,
                },
            };

            if (hasDiff<PatchRegCustomer>(regCustomer, patchPayload)) {
                try {
                    await patchRegCustomer(patchPayload).unwrap();
                } catch {
                    alertManager.error(t.registration.errors.serverError);
                    return;
                }
            }

            onNext();
        })();
    }, [handleSubmit, isPatching, onNext, patchRegCustomer, regCustomer]);

    if (isLoading) {
        return (
            <Center py={10} flexDirection="column" gap={4}>
                <Spinner size="lg" color="yellow.600" />
                <Text fontSize="md" color="gray.700" textAlign="center">
                    {t.registration.form.loadingRegCustomerData}
                </Text>
            </Center>
        );
    }

    return (
        <VStack align="stretch" w="full" h="full" minH={0} p={1}>
            <VStack
                gap={4}
                align="stretch"
                flex={1}
                minH={0}
                overflowY="auto"
                p={1}
                css={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <Text fontSize="sm" color="gray.600">
                    {t.registration.form.addressForwardingHint}
                </Text>
                <Field.Root invalid={!!errors.country}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.countryLabel}
                    </Field.Label>
                    <BaseSelect
                        collection={countryCollection}
                        value={selectedCountry}
                        onValueChange={(value) => {
                            setValue('country', value, { shouldValidate: true, shouldDirty: true });
                        }}
                        variant="white"
                        w="full"
                        disabled={isPatching}
                    />
                    {errors.country && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.country.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root invalid={!!errors.city}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.cityLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.cityPlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.city ? 'red' : 'gray'}
                        {...register('city')}
                    />
                    {errors.city && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.city.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root invalid={!!errors.line}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.lineLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.linePlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.line ? 'red' : 'gray'}
                        {...register('line')}
                    />
                    {errors.line && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.line.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root invalid={!!errors.postcode}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.postcodeLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.postcodePlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.postcode ? 'red' : 'gray'}
                        {...register('postcode')}
                    />
                    {errors.postcode && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.postcode.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>
            </VStack>

            <HStack gap={3} justify="space-between" w="full" flexShrink={0} pt={2}>
                <Button buttonType="secondary" size="lg" flex={1} onClick={onBack}>
                    {t.registration.form.back}
                </Button>
                <Button
                    buttonType="success"
                    size="lg"
                    flex={1}
                    isLoading={isPatching}
                    onClick={handleNextClick}
                >
                    {t.registration.form.registerButton}
                </Button>
            </HStack>
        </VStack>
    );
};
